import { useEffect, useState } from "react";
import { Alert, Badge, Button, Group, Paper, Radio, Stack, Text } from "@mantine/core";
import { IconSend } from "@tabler/icons-react";
import { StudentPersona } from "@features/personas/components/StudentPersona";
import { useResolveStudents } from "@features/communications/queries";
import { ConversationLinkFields } from "@features/conversations/components/ConversationLinkFields";
import {
	emailConversationTarget,
	type ConversationLinkSelection,
} from "@features/conversations/emailConversationTarget";
import { useSendTeamsMessageProxy } from "../query";
import type { TeamsSendRequest } from "../api";
import { TeamsMessageEditor } from "./TeamsMessageEditor";
import { useTeamsMessageEditor } from "./useTeamsMessageEditor";

export function SendStudentTeamsMessageForm({
	studentNo,
	onSuccess,
	onBusyChange,
}: {
	studentNo: string;
	onSuccess?: () => void;
	onBusyChange?: (busy: boolean) => void;
}) {
	const send = useSendTeamsMessageProxy();
	const editor = useTeamsMessageEditor(send.isPending);
	const resolution = useResolveStudents({
		selectors: [`student:${studentNo}`],
	});
	const student = resolution.data?.students.find(
		(student) => String(student.studentNumber) === studentNo,
	);
	const [linkMode, setLinkMode] = useState<"none" | "existing" | "new">("none");
	const [selection, setSelection] = useState<ConversationLinkSelection>({
		ids: [],
		typeId: null,
		classId: null,
		requiresClass: false,
		title: null,
		message: "",
	});
	const [error, setError] = useState<string | null>(null);
	useEffect(() => {
		onBusyChange?.(send.isPending);
	}, [send.isPending, onBusyChange]);
	const unavailable = resolution.isSuccess && !student?.entraObjectId;
	async function submit(event: React.FormEvent) {
		event.preventDefault();
		if (send.isPending || resolution.isFetching || !student?.entraObjectId)
			return;
		if (!editor?.getText().trim()) {
			setError("Enter a message.");
			return;
		}
		setError(null);
		const request: TeamsSendRequest = {
			requestId: crypto.randomUUID(),
			type: "oneOnOne",
			sendAs: "self",
			contentType: "html",
			studentRecipients: [studentNo],
			staffRecipients: [],
			message: editor.getHTML(),
		};
		if (linkMode !== "none") {
			try {
				request.conversationLink = {
					studentNumber: studentNo,
					...emailConversationTarget(linkMode, selection, "Teams message"),
				};
			} catch (cause) {
				setError(
					cause instanceof Error ? cause.message : "Choose a conversation.",
				);
				return;
			}
		}
		send.mutate(request, {
			onSuccess: (result) => {
				if (result.sentCount === 0) return;
				editor.commands.setContent("");
				onSuccess?.();
			},
		});
	}
	return (
		<form onSubmit={submit}>
			<Stack>
				<Paper withBorder p="sm">
					<StudentPersona id={studentNo} hoverable={false} clickable={true}/>
				</Paper>
				{resolution.isError && (
					<Alert color="red">
						The student's Teams details could not be loaded. Try opening this
						dialog again.
					</Alert>
				)}
				{unavailable && (
					<Alert color="red">
						This student does not have a Teams account configured.
					</Alert>
				)}
				<Stack gap={5}>
					<Text size="sm" fw={500}>
						Message
					</Text>
					<TeamsMessageEditor editor={editor} />
				</Stack>
				<Paper withBorder p="sm">
					<Stack gap="sm">
						<Radio.Group
							label="Conversation"
							value={linkMode}
							onChange={(value) => setLinkMode(value as typeof linkMode)}
						>
							<Stack gap={5} mt={5}>
								<Radio
									value="none"
									label="Don't link"
									disabled={send.isPending}
								/>
								<Radio
									value="existing"
									label="Link to existing conversation"
									disabled={send.isPending}
								/>
								<Radio
									value="new"
									label="Create a new conversation"
									disabled={send.isPending}
								/>
							</Stack>
						</Radio.Group>
						{linkMode !== "none" && (
							<ConversationLinkFields
								studentNo={studentNo}
								mode={linkMode}
								value={selection}
								onChange={setSelection}
								disabled={send.isPending}
								defaultTitle="Teams message"
							/>
						)}
					</Stack>
				</Paper>
				{error && <Alert color="red">{error}</Alert>}
				<Button
					type="submit"
					leftSection={<IconSend size={16} />}
					loading={send.isPending}
					disabled={
						send.isPending || resolution.isFetching || !student?.entraObjectId
					}
				>
					Send message
				</Button>
			</Stack>
		</form>
	);
}
